import styled from 'styled-components';
import { Colors, Spacing, FontSize } from '../../../assets/GlobalStyle/Variables';

export const PageWrapper = styled.div`
  padding-top: ${Spacing(4)};
`;

// ─── Filter bar ────────────────────────────────────────────────────────────────

export const FilterBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${Spacing(4)};
  flex-wrap: wrap;
  margin-bottom: ${Spacing(6)};
  padding: ${Spacing(4)};
  background-color: ${Colors.disabled};
  border-radius: 8px;
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(1)};
`;

export const FilterLabel = styled.label`
  font-size: ${FontSize.medium};
  font-weight: 600;
  color: ${Colors.mainText};
`;

export const FilterSelect = styled.select`
  padding: ${Spacing(2)} ${Spacing(3)};
  border: 1px solid ${Colors.lightGrey};
  border-radius: 4px;
  font-size: ${FontSize.normal};
  color: ${Colors.mainText};
  background-color: ${Colors.white};
  cursor: pointer;
  min-width: 220px;

  &:focus {
    outline: 2px solid ${Colors.secondary};
    outline-offset: 1px;
  }
`;

export const FilterButton = styled.button`
  padding: ${Spacing(2)} ${Spacing(5)};
  background-color: ${Colors.primary};
  color: ${Colors.white};
  border: none;
  border-radius: 4px;
  font-size: ${FontSize.normal};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: ${Colors.secondary};
  }
`;

// ─── Results tab ──────────────────────────────────────────────────────────────

export const ResultsLayout = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: ${Spacing(4)};
  min-height: 400px;
`;

export const ResultsList = styled.div`
  border: 1px solid ${Colors.lightGrey};
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const ResultsListHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  background-color: ${Colors.primary};
  color: ${Colors.white};
  font-size: ${FontSize.medium};
  font-weight: 600;
`;

export const ResultsHeaderCell = styled.div`
  padding: ${Spacing(2)} ${Spacing(3)};
  border-right: 1px solid rgba(255, 255, 255, 0.2);

  &:last-child {
    border-right: none;
  }
`;

export const ResultsListBody = styled.div`
  overflow-y: auto;
  max-height: 500px;
  flex: 1;
`;

export const ResultRow = styled.div<{ $selected?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  cursor: pointer;
  border-bottom: 1px solid ${Colors.lightGrey};
  background-color: ${Colors.white};
  border-left: 3px solid ${({ $selected }) => ($selected ? Colors.secondary : 'transparent')};
  font-size: ${FontSize.medium};

  &:hover {
    background-color: ${Colors.disabled};
  }
`;

export const ResultCell = styled.div`
  padding: ${Spacing(2)} ${Spacing(3)};
  border-right: 1px solid ${Colors.lightGrey};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:last-child {
    border-right: none;
  }
`;

export const ResultsDetail = styled.div`
  border: 1px solid ${Colors.lightGrey};
  border-radius: 8px;
  padding: ${Spacing(4)};
  background-color: ${Colors.white};
  overflow-y: auto;
  max-height: 600px;
`;

export const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing(3)};
  padding-bottom: ${Spacing(3)};
  border-bottom: 1px solid ${Colors.lightGrey};

  h3 {
    margin: 0;
    font-size: ${FontSize.subTitle};
    color: ${Colors.primary};
  }
`;

export const DetailMeta = styled.div`
  margin-bottom: ${Spacing(4)};
  font-size: ${FontSize.medium};
  color: ${Colors.mainText};

  p {
    margin: ${Spacing(1)} 0;
  }
`;

export const ParameterResultsTitle = styled.h4`
  font-size: ${FontSize.normal};
  color: ${Colors.primary};
  margin: 0 0 ${Spacing(2)} 0;
`;

export const ParameterResultItem = styled.div<{ $outOfRange?: boolean }>`
  display: grid;
  grid-template-columns: 40% 1fr;
  gap: ${Spacing(2)};
  padding: ${Spacing(2)} ${Spacing(3)};
  margin-bottom: 2px;
  border-radius: 4px;
  font-size: ${FontSize.medium};
  background-color: ${({ $outOfRange }) => ($outOfRange ? '#fff5f5' : Colors.disabled)};
  border-left: 3px solid ${({ $outOfRange }) => ($outOfRange ? Colors.error : 'transparent')};
`;

export const ParameterName = styled.div`
  font-weight: 600;
  color: ${Colors.mainText};
`;

export const ParameterValue = styled.div`
  color: ${Colors.mainText};
`;

export const OutOfRangeBadge = styled.span`
  display: inline-block;
  margin-left: ${Spacing(2)};
  padding: 1px ${Spacing(2)};
  background-color: ${Colors.error};
  color: ${Colors.white};
  border-radius: 3px;
  font-size: ${FontSize.small};
  font-weight: 600;
`;

export const CommentValue = styled.em`
  color: ${Colors.grey};
`;

export const NoSelection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${Colors.grey};
  font-size: ${FontSize.normal};
`;

// ─── Trends tab ───────────────────────────────────────────────────────────────

export const TrendsLayout = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: ${Spacing(4)};
`;

export const TrendsLeft = styled.div`
  border: 1px solid ${Colors.lightGrey};
  border-radius: 8px;
  overflow: hidden;
  overflow-y: auto;
  max-height: 80vh;
`;

export const TrendsRight = styled.div`
  overflow-y: auto;
  max-height: 80vh;
`;

export const TrendsActionsBar = styled.div`
  display: flex;
  gap: ${Spacing(2)};
  margin-bottom: ${Spacing(3)};
  flex-wrap: wrap;
`;

export const AreaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing(3)} ${Spacing(4)};
  background-color: ${Colors.white};
  border-left: 4px solid ${Colors.secondary};
  border-bottom: 1px solid ${Colors.lightGrey};
  color: ${Colors.primary};
  cursor: pointer;
  font-weight: 700;
  font-size: ${FontSize.normal};

  &:hover {
    background-color: ${Colors.disabled};
  }
`;

export const ExpandIcon = styled.span<{ $expanded?: boolean }>`
  transition: transform 0.2s ease;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  display: inline-block;
`;

export const AreaContent = styled.div`
  padding: ${Spacing(3)};
  background-color: ${Colors.disabled};
`;

export const ParameterSection = styled.div`
  margin-bottom: ${Spacing(2)};
  border: 1px solid ${Colors.lightGrey};
  border-radius: 6px;
  overflow: hidden;
  background-color: ${Colors.white};
`;

export const ParameterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing(2)} ${Spacing(3)};
  background-color: ${Colors.white};
  border-bottom: 1px solid ${Colors.lightGrey};
`;

export const ParameterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing(2)};
  font-size: ${FontSize.medium};
  font-weight: 600;
  color: ${Colors.mainText};
`;

export const MiniTable = styled.div`
  font-size: ${FontSize.medium};
`;

export const MiniTableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 80px;
  background-color: ${Colors.lightGrey};
  color: ${Colors.mainText};
  font-weight: 600;
`;

export const MiniTableRow = styled.div<{ $outOfRange?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr 80px;
  border-bottom: 1px solid ${Colors.lightGrey};
  background-color: ${({ $outOfRange }) => ($outOfRange ? '#fff5f5' : 'transparent')};
  color: ${({ $outOfRange }) => ($outOfRange ? Colors.error : Colors.mainText)};
`;

export const MiniTableCell = styled.div`
  padding: ${Spacing(1)} ${Spacing(2)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ChartContainer = styled.div`
  margin-bottom: ${Spacing(6)};
  padding: ${Spacing(4)};
  border: 1px solid ${Colors.lightGrey};
  border-radius: 8px;
  background-color: ${Colors.white};

  h4 {
    margin: 0 0 ${Spacing(3)} 0;
    font-size: ${FontSize.normal};
    color: ${Colors.primary};
  }
`;

export const NoChartsMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${Colors.grey};
  font-size: ${FontSize.normal};
  border: 1px dashed ${Colors.lightGrey};
  border-radius: 8px;
`;

export const SecondaryButton = styled.button`
  padding: ${Spacing(2)} ${Spacing(4)};
  background-color: ${Colors.white};
  color: ${Colors.primary};
  border: 1px solid ${Colors.primary};
  border-radius: 4px;
  font-size: ${FontSize.medium};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: ${Colors.lightBackground};
  }
`;

export const SmallButton = styled.button`
  padding: ${Spacing(1)} ${Spacing(3)};
  background-color: ${Colors.white};
  color: ${Colors.secondary};
  border: 1px solid ${Colors.secondary};
  border-radius: 3px;
  font-size: ${FontSize.small};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: ${Colors.lightBackground};
  }
`;

export const CopyButton = styled.button`
  padding: ${Spacing(2)} ${Spacing(4)};
  background-color: ${Colors.white};
  color: ${Colors.primary};
  border: 1px solid ${Colors.lightGrey};
  border-radius: 4px;
  font-size: ${FontSize.medium};
  cursor: pointer;

  &:hover {
    border-color: ${Colors.primary};
  }
`;

export const LoadingMessage = styled.div`
  padding: ${Spacing(8)};
  text-align: center;
  color: ${Colors.grey};
  font-size: ${FontSize.normal};
`;

export const EmptyMessage = styled.div`
  padding: ${Spacing(8)};
  text-align: center;
  color: ${Colors.grey};
  font-size: ${FontSize.normal};
`;
